import Tabs from "../../components/General/ResponsiveTabs/ResponsiveTabs";
import Dialog from "../../components/General/ConfirmableDialog";
import { useState, useEffect, useMemo } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  CircularProgress,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Paper,
  Chip,
  Grid,
  Stack,
  Tooltip,
  FormControlLabel,
  Switch,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  ExpandMore,
  Edit,
  Add,
  DeleteForever,
  InfoOutlined,
  SwapHoriz,
} from "@mui/icons-material";
import axios from "axios";
import useAuth from "../../hooks/useAuth";
import { apiVariable } from "../../api/apiVariable";
import {
  TimeOffPolicyDescriptions,
  TimeOffPolicyEnum,
} from "../../enums/HR/TimeOffPolicyEnum";
import { useParams } from "react-router-dom";
import StatusSnackbar from "../../components/General/StatusSnackbar/StatusSnackbar";
import { getPublicHolidays } from "../../data/HR/PublicHolidays";
import { toDateOnlyString } from "../../helpers/parseDateOnly";
import { format } from "date-fns";
import { camelCaseToSentenceCase } from "../../helpers/camelCaseToSentenceCase";
import { capitalizeFirstChar } from "../../helpers/formatingFunctions";
import { EmployeeDto } from "../../interfaces/HR/employee";
import {
  TimeOffDayPortion,
  normalizeTimeOffDayPortion,
  timeOffDayPortionLabel,
} from "../../enums/HR/TimeOffDayPortionEnum";

const WORKING_HOURS_PER_DAY = Number(
  process.env.REACT_APP_WORKING_HOURS_PER_DAY ?? 8,
);

const daysToHours = (days: number) => days * WORKING_HOURS_PER_DAY;
const hoursToDays = (hours: number) => hours / WORKING_HOURS_PER_DAY;
const formatHours = (hours: number) => hours.toFixed(3);

const calculationTypeLabels: Record<number, string> = {
  1: "Opening Balance",
  2: "Accrual",
  3: "Usage",
  4: "Reversal",
  5: "Manual Adjustment",
};

interface TimeOffCalculation {
  id: string;
  type: string | number;
  calculationDate: string;
  days: number;
  balanceAfter: number;
  note?: string | null;
  description?: string | null;
}

const getCalculationTypeLabel = (type: string | number) => {
  const numericType = Number(type);
  if (!Number.isNaN(numericType) && calculationTypeLabels[numericType]) {
    return calculationTypeLabels[numericType];
  }

  return camelCaseToSentenceCase(type.toString());
};

const CYCLE_HISTORY_PREVIEW_COUNT = 5;

const CycleHistory = ({
  calculations,
  displayInHours,
}: {
  calculations: TimeOffCalculation[];
  displayInHours: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const sortedCalculations = useMemo(
    () =>
      [...calculations].sort(
        (left, right) =>
          new Date(right.calculationDate).getTime() -
          new Date(left.calculationDate).getTime(),
      ),
    [calculations],
  );
  const hasMoreCalculations =
    sortedCalculations.length > CYCLE_HISTORY_PREVIEW_COUNT;
  const displayedCalculations = isExpanded
    ? sortedCalculations
    : sortedCalculations.slice(0, CYCLE_HISTORY_PREVIEW_COUNT);

  return (
    <Stack spacing={1}>
      {displayedCalculations.map((calculation) => {
        const displayDays = displayInHours
          ? `${formatHours(daysToHours(calculation.days))} hours`
          : `${calculation.days} day${calculation.days === 1 ? "" : "s"}`;
        const displayBalance = displayInHours
          ? `${formatHours(daysToHours(calculation.balanceAfter))} hours`
          : `${calculation.balanceAfter} days`;
        const details = calculation.description || calculation.note;

        return (
          <Paper
            key={calculation.id}
            variant="outlined"
            sx={{ p: 1.5, borderRadius: 2 }}
          >
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" fontWeight={600}>
                  {getCalculationTypeLabel(calculation.type)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(calculation.calculationDate).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={2}>
                <Typography variant="caption" color="text.secondary">
                  Change
                </Typography>
                <Typography variant="body2">{displayDays}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">
                  Balance after
                </Typography>
                <Typography variant="body2">{displayBalance}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  {details || "No additional details"}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        );
      })}
      {hasMoreCalculations && (
        <Button
          onClick={() => setIsExpanded((expanded) => !expanded)}
          aria-expanded={isExpanded}
          sx={{ alignSelf: "flex-start" }}
        >
          {isExpanded ? "View less" : "View more"}
        </Button>
      )}
    </Stack>
  );
};

const toComparableDate = (value: string | number | Date) => {
  if (typeof value === "string") {
    const dateOnlyMatch = value.match(/^\d{4}-\d{2}-\d{2}/);
    if (dateOnlyMatch) return dateOnlyMatch[0];
  }

  return toDateOnlyString(new Date(value));
};

const TimeOffPage = () => {
  const { token, role } = useAuth();
  const { id } = useParams<{ id: string }>();

  const [employee, setEmployee] = useState<EmployeeDto | null>(null);

  interface TimeOff {
    startDate: string | number | Date;
    endDate: string | number | Date;
    status: string;
    reviewer: string;
    notes: any;
    id: string;
    policy: string;
    daysTaken: number;
    currentBalance: number;
    currentCycleStart: string;
    currentCycleEnd: string;
    timeOffCalculations: TimeOffCalculation[];
    isHalfDay?: boolean;
    dayPortion?: TimeOffDayPortion | string | number;
  }

  const [employeeTimeOffs, setEmployeeTimeOffs] = useState<TimeOff[]>([]);
  const [timeOffRequests, setTimeOffRequests] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [openRequestDialog, setOpenRequestDialog] = useState(false);
  const [openSwapDialog, setOpenSwapDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const [publicHolidayTab, setPublicHolidayTab] = useState<"swap" | "dayOff">(
    "swap",
  );

  const [requestData, setRequestData] = useState({
    policy: "",
    startDate: "",
    endDate: "",
    notes: "",
    dayPortions: ["fullDay"] as TimeOffDayPortion[],
    allowPastStartDate: false,
  });
  const swapInitialState = {
    originalDate: null as Date | null,
    swapDate: null as Date | null,
    notes: "",
  };
  const dayOffInitialState = {
    date: null as Date | null,
    notes: "",
  };

  const [swapData, setSwapData] = useState(swapInitialState);
  const [dayOffData, setDayOffData] = useState(dayOffInitialState);
  const [swapNotesEdited, setSwapNotesEdited] = useState(false);
  const [dayOffNotesEdited, setDayOffNotesEdited] = useState(false);
  const [editData, setEditData] = useState({
    id: "",
    policy: "",
    daysTaken: "",
    currentBalance: "",
  });
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(
    null,
  );
  const normalizedDeliveryUnit = employee?.deliveryUnit
    ? employee.deliveryUnit.trim().toLowerCase()
    : "";
  const normalizedContractType = employee?.contractType
    ? employee.contractType.trim().toLowerCase()
    : "";
  const canSelectDayPortion =
    normalizedContractType === "w2" ||
    normalizedContractType === "f1099" ||
    normalizedContractType === "1099";
  const isUSW2Employee =
    normalizedDeliveryUnit === "us" && normalizedContractType === "w2";
  const isUS1099Employee =
    normalizedDeliveryUnit === "us" &&
    (normalizedContractType === "f1099" || normalizedContractType === "1099");
  const isUSEmployee = normalizedDeliveryUnit === "us";
  const isMoldova1099Employee =
    normalizedDeliveryUnit === "md" &&
    (normalizedContractType === "f1099" || normalizedContractType === "1099");
  const availablePolicyEntries = useMemo(() => {
    const entries = Object.entries(TimeOffPolicyEnum).filter(
      ([policy]) => policy !== "shortDay",
    );

    if (!employee) {
      return entries;
    }

    const isUSDeliveryUnit = normalizedDeliveryUnit === "us";
    const isMoldovaDeliveryUnit = normalizedDeliveryUnit === "md";

    if (isUSDeliveryUnit) {
      if (normalizedContractType === "w2") {
        const allowedPolicies = new Set([
          "paidTimeOff",
          "unpaidLeave",
          "floatingHolidays",
          "workFromHome",
        ]);

        return entries.filter(([policy]) => allowedPolicies.has(policy));
      }

      if (
        normalizedContractType === "f1099" ||
        normalizedContractType === "1099"
      ) {
        const allowedPolicies = new Set(["unpaidLeave", "workFromHome"]);

        return entries.filter(([policy]) => allowedPolicies.has(policy));
      }
    }

    if (isMoldovaDeliveryUnit) {
      if (
        normalizedContractType === "f1099" ||
        normalizedContractType === "1099"
      ) {
        const allowedPolicies = new Set(["unpaidLeave", "workFromHome"]);

        return entries.filter(([policy]) => allowedPolicies.has(policy));
      }

      return entries.filter(
        ([policy]) => policy !== "paidTimeOff" && policy !== "floatingHolidays",
      );
    }

    return entries.filter(([policy]) => policy !== "floatingHolidays");
  }, [employee, normalizedContractType, normalizedDeliveryUnit]);

  useEffect(() => {
    if (!requestData.policy) return;

    const policyIsAvailable = availablePolicyEntries.some(
      ([policy]) => policy === requestData.policy,
    );

    if (!policyIsAvailable) {
      setRequestData((prev) => ({
        ...prev,
        policy: "",
        dayPortions: ["fullDay"],
      }));
    }
  }, [availablePolicyEntries, requestData.policy]);

  useEffect(() => {
    if (!canSelectDayPortion && !requestData.dayPortions.includes("fullDay")) {
      setRequestData((prev) => ({ ...prev, dayPortions: ["fullDay"] }));
    }
  }, [canSelectDayPortion, requestData.dayPortions]);

  const isHalfDayRequest = !requestData.dayPortions.includes("fullDay");

  useEffect(() => {
    if (isHalfDayRequest && requestData.startDate !== requestData.endDate) {
      setRequestData((prev) => ({ ...prev, endDate: prev.startDate }));
    }
  }, [requestData.endDate, isHalfDayRequest, requestData.startDate]);

  const portionsAlreadyRequested = useMemo(() => {
    if (!requestData.startDate) return new Set<TimeOffDayPortion>();

    return new Set<TimeOffDayPortion>(
      timeOffRequests
        .filter((request) => {
          const status = request.status?.toString().toLowerCase();
          const requestStart = toComparableDate(request.startDate);
          const requestEnd = toComparableDate(request.endDate);
          return (
            status !== "declined" &&
            status !== "rejected" &&
            !!requestStart &&
            !!requestEnd &&
            requestStart <= requestData.startDate &&
            requestEnd >= requestData.startDate
          );
        })
        .map((request) =>
          normalizeTimeOffDayPortion(request.dayPortion, request.isHalfDay),
        ),
    );
  }, [requestData.startDate, timeOffRequests]);

  const hasFullDayConflict = portionsAlreadyRequested.has("fullDay");

  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isSwapOriginalPickerOpen, setIsSwapOriginalPickerOpen] =
    useState(false);
  const [isSwapDatePickerOpen, setIsSwapDatePickerOpen] = useState(false);
  const [isDayOffPickerOpen, setIsDayOffPickerOpen] = useState(false);

  const disableNonHoliday = (date: Date) =>
    !getPublicHolidays(
      date.getFullYear(),
      employee?.deliveryUnit?.trim().toUpperCase() === "US" ? "USA" : "Moldova",
    ).some((holiday) => holiday.date === toDateOnlyString(date));

  useEffect(() => {
    if (swapNotesEdited) return;

    if (swapData.originalDate && swapData.swapDate) {
      const defaultText = `Swapping ${format(
        swapData.originalDate,
        "MM/dd/yyyy",
      )} with ${format(swapData.swapDate, "MM/dd/yyyy")}`;
      setSwapData((prev) =>
        prev.notes === defaultText ? prev : { ...prev, notes: defaultText },
      );
    } else if (swapData.notes) {
      setSwapData((prev) => ({ ...prev, notes: "" }));
    }
  }, [
    swapData.originalDate,
    swapData.swapDate,
    swapData.notes,
    swapNotesEdited,
  ]);

  useEffect(() => {
    if (dayOffNotesEdited) return;

    if (dayOffData.date) {
      const defaultText = `I will be off for public holiday on : ${format(
        dayOffData.date,
        "MM/dd/yyyy",
      )}`;
      setDayOffData((prev) =>
        prev.notes === defaultText ? prev : { ...prev, notes: defaultText },
      );
    } else if (dayOffData.notes) {
      setDayOffData((prev) => ({ ...prev, notes: "" }));
    }
  }, [dayOffData.date, dayOffData.notes, dayOffNotesEdited]);

  const resetPublicHolidayState = () => {
    setPublicHolidayTab("swap");
    setSwapData({ ...swapInitialState });
    setDayOffData({ ...dayOffInitialState });
    setSwapNotesEdited(false);
    setDayOffNotesEdited(false);
    setIsSwapOriginalPickerOpen(false);
    setIsSwapDatePickerOpen(false);
    setIsDayOffPickerOpen(false);
  };

  const closePublicHolidayDialog = () => {
    resetPublicHolidayState();
    setOpenSwapDialog(false);
  };

  const handleSnackbarClose = () => {
    setStatus("idle");
    setSuccessMessage("");
    setErrorMessage(null);
  };

  const PolicyBalanceChip = ({
    policy,
    currentBalance,
    displayInHours,
  }: {
    policy: string;
    currentBalance: number;
    displayInHours: boolean;
  }) => {
    const finitePolicies = new Set([
      "vacation",
      "additionalHolidayForParentsWithMoreThanOneKid",
      "paidTimeOff",
      "floatingHolidays",
    ]);

    const label = finitePolicies.has(policy)
      ? displayInHours
        ? `${formatHours(daysToHours(currentBalance))} hours left`
        : `${currentBalance} day${currentBalance === 1 ? "" : "s"} left`
      : displayInHours
        ? "∞ hours available"
        : "∞ balance";

    return <Chip size="small" color="secondary" label={label} sx={{ ml: 1 }} />;
  };

  const SectionHeader = ({ title }: { title: string }) => (
    <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
      {title}
    </Typography>
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const empRes = await axios.get(`${apiVariable}/getEmployeeById/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const emp = empRes.data;
        setEmployee(emp);

        const offRes = await axios.get(
          `${apiVariable}/getEmployeeTimeOff/${emp.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setEmployeeTimeOffs(offRes.data);

        const reqRes = await axios.get(`${apiVariable}/employee/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTimeOffRequests(reqRes.data);
      } catch (err) {
        console.error(err);
        setStatus("error");
        setErrorMessage("Failed to load data.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [token, id]);

  const handleOpenDeleteModal = (reqId: string) => {
    setSelectedRequestId(reqId);
    setOpenDeleteModal(true);
  };

  const handleDeleteRequest = async () => {
    if (!selectedRequestId) return;
    try {
      await axios.delete(`${apiVariable}/timeOffRequest/${selectedRequestId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTimeOffRequests((prev) =>
        prev.filter((r) => r.id !== selectedRequestId),
      );
      setStatus("success");
      setSuccessMessage("Request deleted successfully.");
    } catch (err: any) {
      setStatus("error");
      setErrorMessage(err.response?.data || "Failed to delete the request.");
    } finally {
      setOpenDeleteModal(false);
      setSelectedRequestId(null);
    }
  };

  const handleSubmitRequest = async () => {
    setSubmitLoading(true);
    try {
      if (!employee) throw new Error("Employee data not loaded");

      const policyIsAvailable = availablePolicyEntries.some(
        ([policy]) => policy === requestData.policy,
      );

      if (!policyIsAvailable) {
        throw new Error("Selected policy is not available for this employee.");
      }

      const requestedPortions = canSelectDayPortion
        ? requestData.dayPortions
        : (["fullDay"] as TimeOffDayPortion[]);
      if (requestedPortions.length === 0 || requestedPortions.length > 2) {
        throw new Error("Select a full day or up to two different half-days.");
      }
      if (
        hasFullDayConflict ||
        requestedPortions.some((portion) =>
          portionsAlreadyRequested.has(portion),
        ) ||
        (requestedPortions.includes("fullDay") &&
          portionsAlreadyRequested.size > 0)
      ) {
        throw new Error(
          "This selection duplicates or exceeds the available day for this date.",
        );
      }

      const body = {
        employeeId: employee.id,
        policy: requestData.policy,
        startDate: requestData.startDate,
        endDate: requestData.endDate,
        notes: requestData.notes,
        allowPastStartDate: requestData.allowPastStartDate,
      };

      await Promise.all(
        requestedPortions.map((dayPortion) =>
          axios.post(
            `${apiVariable}/timeOffRequests`,
            {
              ...body,
              isHalfDay: dayPortion !== "fullDay",
              dayPortion,
            },
            { headers: { Authorization: `Bearer ${token}` } },
          ),
        ),
      );

      const [offRes, reqRes] = await Promise.all([
        axios.get(`${apiVariable}/getEmployeeTimeOff/${employee.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${apiVariable}/employee/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setEmployeeTimeOffs(offRes.data);
      setTimeOffRequests(reqRes.data);

      setStatus("success");
      setSuccessMessage("Time off request submitted successfully.");
      setOpenRequestDialog(false);
      setRequestData({
        policy: "",
        startDate: "",
        endDate: "",
        notes: "",
        dayPortions: ["fullDay"],
        allowPastStartDate: false,
      });
    } catch (err: any) {
      console.error(err);
      setStatus("error");
      setErrorMessage(
        err.response?.data || "Failed to submit time off request.",
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSwapSubmit = async () => {
    setSubmitLoading(true);
    try {
      if (!employee) throw new Error("Employee data not loaded");
      const isSwapMode = publicHolidayTab === "swap";
      const body: Record<string, any> = {
        employeeId: employee.id,
        originalDate: toDateOnlyString(
          isSwapMode ? swapData.originalDate : dayOffData.date,
        ),
        swapDate: toDateOnlyString(
          isSwapMode ? swapData.swapDate : dayOffData.date,
        ),
        notes: isSwapMode ? swapData.notes : dayOffData.notes,
      };

      if (!isSwapMode) {
        body.isDayOff = true;
      }
      const { data } = await axios.post(
        `${apiVariable}/swapPublicHoliday`,
        body,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setTimeOffRequests((prev) => [...prev, data]);
      setStatus("success");
      setSuccessMessage(
        isSwapMode
          ? "Public holiday swap submitted successfully."
          : "Public holiday day off submitted successfully.",
      );
      closePublicHolidayDialog();
    } catch (err: any) {
      setStatus("error");
      setErrorMessage(err.response?.data || "Failed to swap public holiday.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEditSubmit = async () => {
    const displayedTaken = parseFloat(editData.daysTaken);
    const displayedBalance = parseFloat(editData.currentBalance);
    if (isNaN(displayedTaken) || isNaN(displayedBalance)) {
      setStatus("error");
      setErrorMessage("Please enter valid numeric values.");
      return;
    }

    const daysTakenNum = isUSEmployee
      ? hoursToDays(displayedTaken)
      : displayedTaken;
    const balanceNum = isUSEmployee
      ? hoursToDays(displayedBalance)
      : displayedBalance;

    try {
      await axios.put(
        `${apiVariable}/modifyTimeOffDays`,
        {
          id: editData.id,
          daysTaken: daysTakenNum,
          currentBalance: balanceNum,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const offRes = await axios.get(
        `${apiVariable}/getEmployeeTimeOff/${employee?.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setEmployeeTimeOffs(offRes.data);

      setStatus("success");
      setSuccessMessage("Time off updated successfully.");
      setOpenEditDialog(false);
    } catch (err: any) {
      setStatus("error");
      setErrorMessage(err.response?.data || "Failed to update time off data.");
    }
  };

  const groupedRequests = timeOffRequests.reduce(
    (acc: Record<string, TimeOff[]>, req: any) => {
      acc[req.policy] = acc[req.policy] || [];
      acc[req.policy].push(req);
      return acc;
    },
    {},
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          flexWrap: { xs: "wrap", lg: "nowrap" },
          display: "flex",
          justifyContent: "center",
          mt: 8,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, pb: 10, maxWidth: 900, mx: "auto" }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mt: 4, borderRadius: 3 }}>
        <Typography variant="h4" align="center" sx={{ fontWeight: 700 }}>
          {employee?.firstName} {employee?.lastName} – Time Off
        </Typography>

        <Box
          sx={{
            flexWrap: { xs: "wrap", lg: "nowrap" },
            display: "flex",
            justifyContent: "center",
            mt: 2,
            gap: 2,
          }}
        >
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpenRequestDialog(true)}
          >
            Request Time Off
          </Button>
          {!isUSW2Employee && !isUS1099Employee && !isMoldova1099Employee && (
            <Button
              variant="outlined"
              startIcon={<SwapHoriz />}
              onClick={() => {
                resetPublicHolidayState();
                setOpenSwapDialog(true);
              }}
            >
              Public Holiday
            </Button>
          )}
        </Box>
      </Paper>

      <SectionHeader title="Balances" />
      {employeeTimeOffs
        .filter(
          (timeOff) =>
            timeOff.policy !== "shortDay" && timeOff.policy !== "publicHoliday",
        )
        .map((timeOff) => (
          <Accordion key={timeOff.policy} sx={{ mb: 1.5 }}>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                "& .MuiAccordionSummary-content": {
                  flexWrap: "wrap",
                  gap: 1,
                  alignItems: "center",
                },
              }}
            >
              <Box
                sx={{
                  flexBasis: { xs: "100%", sm: "auto" },
                  minWidth: 0,
                  flexWrap: { xs: "wrap", lg: "nowrap" },
                  display: "flex",
                  alignItems: "center",
                  flexGrow: 1,
                }}
              >
                <Typography>{TimeOffPolicyEnum[timeOff.policy]}</Typography>
                <Tooltip
                  title={
                    <Typography sx={{ whiteSpace: "pre-line" }}>
                      {TimeOffPolicyDescriptions[timeOff.policy]}
                    </Typography>
                  }
                  arrow
                >
                  <InfoOutlined fontSize="small" sx={{ ml: 1 }} />
                </Tooltip>
              </Box>
              <Chip
                size="small"
                label={
                  isUSEmployee
                    ? `Taken: ${formatHours(daysToHours(timeOff.daysTaken))} hours`
                    : `Taken: ${timeOff.daysTaken}`
                }
                sx={{ mr: 1 }}
              />
              <PolicyBalanceChip
                policy={timeOff.policy}
                currentBalance={timeOff.currentBalance}
                displayInHours={isUSEmployee}
              />
              {role?.includes("HR") && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditData({
                      id: timeOff.id,
                      policy: timeOff.policy,
                      daysTaken: isUSEmployee
                        ? formatHours(daysToHours(timeOff.daysTaken))
                        : timeOff.daysTaken.toString(),
                      currentBalance: isUSEmployee
                        ? formatHours(daysToHours(timeOff.currentBalance))
                        : timeOff.currentBalance.toString(),
                    });
                    setOpenEditDialog(true);
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              )}
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={1}>
                <Grid item xs={6} sm={4}>
                  <Typography>
                    <strong>Cycle Start:</strong>{" "}
                    {new Date(timeOff.currentCycleStart).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography>
                    <strong>Cycle End:</strong>{" "}
                    {new Date(timeOff.currentCycleEnd).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography>
                    <strong>Annual Allowance:</strong>{" "}
                    {timeOff.policy === "vacation"
                      ? "28 days"
                      : timeOff.policy ===
                          "additionalHolidayForParentsWithMoreThanOneKid"
                        ? "3 days"
                        : timeOff.policy === "paidTimeOff"
                          ? "Limited"
                          : "∞"}
                  </Typography>
                </Grid>
              </Grid>
              {(timeOff.policy === "paidTimeOff" ||
                timeOff.policy === "vacation" ||
                timeOff.policy ===
                  "additionalHolidayForParentsWithMoreThanOneKid") && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Cycle History
                  </Typography>
                  {timeOff.timeOffCalculations?.length ? (
                    <CycleHistory
                      calculations={timeOff.timeOffCalculations}
                      displayInHours={isUSEmployee}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No cycle history is available for this policy.
                    </Typography>
                  )}
                </>
              )}
            </AccordionDetails>
          </Accordion>
        ))}

      <SectionHeader title="Requests" />
      {Object.entries(groupedRequests).map(([policy, requests]) => (
        <Accordion key={policy} sx={{ mb: 1.5 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography sx={{ flexGrow: 1 }}>
              {TimeOffPolicyEnum[policy] || camelCaseToSentenceCase(policy)} (
              {requests.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              {(requests as TimeOff[]).map((request) => (
                <Paper
                  key={request.id}
                  variant="outlined"
                  sx={{ p: 2, position: "relative" }}
                >
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <Typography>
                        <strong>Requested:</strong>{" "}
                        {new Date(request.startDate).toLocaleDateString()} –{" "}
                        {new Date(request.endDate).toLocaleDateString()}
                      </Typography>
                      <Chip
                        size="small"
                        sx={{ mt: 0.5 }}
                        label={timeOffDayPortionLabel(
                          request.dayPortion,
                          request.isHalfDay,
                        )}
                        color={request.isHalfDay ? "secondary" : "default"}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography>
                        <strong>Status:</strong>{" "}
                        {capitalizeFirstChar(request.status)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography>
                        <strong>Reviewer:</strong> {request.reviewer || "N/A"}
                      </Typography>
                    </Grid>
                    {request.notes && (
                      <Grid item xs={12}>
                        <Typography>
                          <strong>Notes:</strong> {request.notes}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                  {(request.status === "pending" ||
                    (request.status === "approved" &&
                      new Date(request.startDate) > new Date() &&
                      new Date(request.endDate) > new Date())) && (
                    <IconButton
                      size="small"
                      color="error"
                      aria-label="Cancel time off request"
                      sx={{ display: "flex", ml: "auto", mt: 1 }}
                      onClick={() => handleOpenDeleteModal(request.id)}
                    >
                      <DeleteForever />
                    </IconButton>
                  )}
                </Paper>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}

      <Dialog
        open={openRequestDialog}
        onClose={() => setOpenRequestDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Request Time Off</DialogTitle>
        <DialogContent>
          <TextField
            select
            label="Policy"
            value={requestData.policy}
            onChange={(e) => {
              const policy = e.target.value;

              setRequestData({
                ...requestData,
                policy,
              });
            }}
            fullWidth
            margin="normal"
          >
            {availablePolicyEntries
              .filter(([value]) => {
                if (value === "publicHoliday") return false;
                return true;
              })
              .map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  <Box
                    sx={{
                      flexWrap: { xs: "wrap", lg: "nowrap" },
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {label}
                    <Tooltip
                      title={
                        <Typography sx={{ whiteSpace: "pre-line" }}>
                          {TimeOffPolicyDescriptions[value]}
                        </Typography>
                      }
                      arrow
                    >
                      <InfoOutlined fontSize="small" sx={{ ml: 1 }} />
                    </Tooltip>
                  </Box>
                </MenuItem>
              ))}
          </TextField>
          <TextField
            label="Start Date"
            type="date"
            value={requestData.startDate}
            onChange={(e) => {
              const startDate = e.target.value;

              setRequestData({
                ...requestData,
                startDate,
                endDate: isHalfDayRequest ? startDate : requestData.endDate,
              });
            }}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />

          {canSelectDayPortion ? (
            <>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Day portion
              </Typography>
              <ToggleButtonGroup
                value={requestData.dayPortions}
                onChange={(_, value: TimeOffDayPortion[]) => {
                  let dayPortions = value;
                  const last = value[value.length - 1];
                  if (last === "fullDay") dayPortions = ["fullDay"];
                  else
                    dayPortions = value.filter(
                      (portion) => portion !== "fullDay",
                    );
                  if (dayPortions.length > 2) return;
                  setRequestData({
                    ...requestData,
                    dayPortions,
                    endDate: dayPortions.includes("fullDay")
                      ? requestData.endDate
                      : requestData.startDate,
                  });
                }}
                aria-label="Day portion"
                fullWidth
              >
                <ToggleButton
                  value="fullDay"
                  disabled={portionsAlreadyRequested.size > 0}
                >
                  Full day
                </ToggleButton>
                <ToggleButton
                  value="firstHalf"
                  disabled={
                    hasFullDayConflict ||
                    portionsAlreadyRequested.has("firstHalf")
                  }
                >
                  First half
                </ToggleButton>
                <ToggleButton
                  value="secondHalf"
                  disabled={
                    hasFullDayConflict ||
                    portionsAlreadyRequested.has("secondHalf")
                  }
                >
                  Second half
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary">
                Select either half independently, or select both halves as two
                distinct entries for one full day.
              </Typography>
            </>
          ) : (
            <></>
          )}
          <TextField
            label="End Date"
            type="date"
            value={requestData.endDate}
            onChange={(e) =>
              setRequestData({ ...requestData, endDate: e.target.value })
            }
            disabled={isHalfDayRequest}
            helperText={
              isHalfDayRequest
                ? "Half-day requests can only cover one date."
                : undefined
            }
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          {role?.includes("HeadOfHR") && (
            <FormControlLabel
              control={
                <Switch
                  checked={requestData.allowPastStartDate}
                  onChange={(e) =>
                    setRequestData({
                      ...requestData,
                      allowPastStartDate: e.target.checked,
                    })
                  }
                />
              }
              label="Allow Past Start Date"
            />
          )}
          <TextField
            label="Notes"
            value={requestData.notes}
            onChange={(e) =>
              setRequestData({ ...requestData, notes: e.target.value })
            }
            fullWidth
            margin="normal"
            multiline
            rows={4}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenRequestDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSubmitRequest}
            variant="contained"
            disabled={
              !requestData.policy ||
              !requestData.startDate ||
              !requestData.endDate ||
              requestData.dayPortions.length === 0 ||
              !requestData.notes ||
              submitLoading
            }
          >
            {submitLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Request"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openSwapDialog}
        onClose={closePublicHolidayDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Public Holiday</DialogTitle>
        <DialogContent>
          <Tabs
            value={publicHolidayTab}
            onChange={(_, newValue) =>
              setPublicHolidayTab(newValue as "swap" | "dayOff")
            }
            sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
          >
            <Tab value="swap" label="Swap public holiday" />
            <Tab value="dayOff" label="Day off on public holiday" />
          </Tabs>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            {publicHolidayTab === "swap" ? (
              <>
                <DatePicker
                  label="I will work on"
                  value={swapData.originalDate}
                  onChange={(newValue) =>
                    setSwapData((prev) => ({ ...prev, originalDate: newValue }))
                  }
                  shouldDisableDate={disableNonHoliday}
                  open={isSwapOriginalPickerOpen}
                  onOpen={() => setIsSwapOriginalPickerOpen(true)}
                  onClose={() => setIsSwapOriginalPickerOpen(false)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      margin: "normal",
                      InputProps: { readOnly: true },
                      onClick: () => setIsSwapOriginalPickerOpen(true),
                      onKeyDown: (event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setIsSwapOriginalPickerOpen(true);
                        }
                      },
                    },
                  }}
                />
                <DatePicker
                  label="Be off on"
                  value={swapData.swapDate}
                  onChange={(newValue) =>
                    setSwapData((prev) => ({ ...prev, swapDate: newValue }))
                  }
                  disablePast
                  open={isSwapDatePickerOpen}
                  onOpen={() => setIsSwapDatePickerOpen(true)}
                  onClose={() => setIsSwapDatePickerOpen(false)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      margin: "normal",
                      InputProps: { readOnly: true },
                      onClick: () => setIsSwapDatePickerOpen(true),
                      onKeyDown: (event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setIsSwapDatePickerOpen(true);
                        }
                      },
                    },
                  }}
                />
              </>
            ) : (
              <DatePicker
                label="I will be off on"
                value={dayOffData.date}
                onChange={(newValue) =>
                  setDayOffData((prev) => ({ ...prev, date: newValue }))
                }
                shouldDisableDate={disableNonHoliday}
                open={isDayOffPickerOpen}
                onOpen={() => setIsDayOffPickerOpen(true)}
                onClose={() => setIsDayOffPickerOpen(false)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: "normal",
                    InputProps: { readOnly: true },
                    onClick: () => setIsDayOffPickerOpen(true),
                    onKeyDown: (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setIsDayOffPickerOpen(true);
                      }
                    },
                  },
                }}
              />
            )}
          </LocalizationProvider>
          {publicHolidayTab === "dayOff" && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ mt: 1 }}
            >
              <TextField
                label="Start Date"
                value={
                  dayOffData.date ? format(dayOffData.date, "MM/dd/yyyy") : ""
                }
                fullWidth
                margin="normal"
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="End Date"
                value={
                  dayOffData.date ? format(dayOffData.date, "MM/dd/yyyy") : ""
                }
                fullWidth
                margin="normal"
                InputProps={{ readOnly: true }}
              />
            </Stack>
          )}
          <TextField
            label="Notes"
            value={
              publicHolidayTab === "swap" ? swapData.notes : dayOffData.notes
            }
            onChange={(e) => {
              const { value } = e.target;
              if (publicHolidayTab === "swap") {
                if (!swapNotesEdited) setSwapNotesEdited(true);
                setSwapData((prev) => ({ ...prev, notes: value }));
              } else {
                if (!dayOffNotesEdited) setDayOffNotesEdited(true);
                setDayOffData((prev) => ({ ...prev, notes: value }));
              }
            }}
            fullWidth
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closePublicHolidayDialog}>Cancel</Button>
          <Button
            onClick={handleSwapSubmit}
            variant="contained"
            disabled={
              submitLoading ||
              (publicHolidayTab === "swap"
                ? !swapData.originalDate ||
                  !swapData.swapDate ||
                  !swapData.notes
                : !dayOffData.date || !dayOffData.notes)
            }
          >
            {submitLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Submit"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Edit Time Off</DialogTitle>
        <DialogContent>
          <TextField
            label={isUSEmployee ? "Hours Taken" : "Days Taken"}
            type="number"
            value={editData.daysTaken}
            onChange={(e) =>
              setEditData({ ...editData, daysTaken: e.target.value })
            }
            fullWidth
            margin="normal"
            inputProps={isUSEmployee ? { step: "0.001" } : undefined}
          />
          <TextField
            label={isUSEmployee ? "Current Balance (hours)" : "Current Balance"}
            type="number"
            value={editData.currentBalance}
            onChange={(e) =>
              setEditData({ ...editData, currentBalance: e.target.value })
            }
            fullWidth
            margin="normal"
            inputProps={isUSEmployee ? { step: "0.001" } : undefined}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSubmit}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this request? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteModal(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteRequest}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <StatusSnackbar
        status={status}
        successMessage={successMessage}
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      />
    </Box>
  );
};

export default TimeOffPage;
